import { createTransform } from 'redux-persist';
import createPersistedReducer from '../lib/createPersistedReducer';
import mergeByProp from '../../lib/mergeByProp';
import { mergeDeepMissing } from '@shared/utils/mergeDeepMissing.js';
import { setDeep } from '@shared/utils/setDeep.js';
import dropPersistedRouteState from './uiReducer/dropPersistedRouteState.js';

const initialState = {
  fixedWidth: false,
  activeRouteId: undefined,
  activeRouteReady: false,
  boundForms: {},
  mobile: document.body.clientWidth < 640,
  prefs: {},
  search: {},
  filters: {
    search: ''
  },
  controls: {},
  aiActionUpdate: {},
  appActionResponseUpdate: {},
  appFixerUpdate: {},
  actionPageRunState: {},
  contextMenu: [],
  contextMenuTarget: null,
  modals: {},
  modalStack: [],
  drawers: {},
  docDrawer: { open: false, docId: undefined },
  appDocDrawer: { open: false },
  argumentOptionSlider: {},
  argumentOptionHasBeenModified: false,
  aiChat: { open: false, messages: [] },
  commentChat: null,
  workflowPanelIsExpanded: false,
  workflowLockTriggered: false,
  workflowZoom: 1,
  workflowPan: { x: 0, y: 0 },
  tempBlock: false,
  isDeploying: {},
  loadState: {},
  codeState: {},
  headerInputState: {},
  params: {},
  websocket: {
    connectionStatus: 'Connecting',
    error: {}
  },
  errorSplashScreen: {
    id: '',
    open: false,
    message: '',
    actions: []
  },
  aiPopoverOpen: { open: false, loading: false },
  connectionPopoverOpen: false,
  externalUpdates: [],
  timezones: [],
  helpContextMap: {},
  highlightPanel: false,
  panelHeaderAlert: false,
  liveWorkflowLogsDrawer: {
    open: false
  },
  appOutputDrawer: {
    open: false,
    statusId: null
  },
  appIssueReportDrawer: {
    open: false
  },
  isToolbarHovered: false,
  isToolbarPopoverOpen: false,
  runStep: false,
  transitionSignUp: false,
  signupBootstrapError: null,
  signupBootstrapRetry: { sequence: 0, stage: null },
  signupState: {
    currentStep: 'email',
    emailAddress: '',
    signupAttempt: null,
    flowStarted: false,
    timestamp: null,
    isOAuth: false
  },
  lookupLoading: {},
  domainDiscovery: [],
  googleSheetsColumns: {},
  useNextWebhookResponse: false
};

const filterTimeouts = {
  datePicker: 1000 * 60 * 60 * 24
};

const persistConfig = {
  key: 'uiReducer',
  // `params` and `activeRouteId` mirror the current URL (synced by
  // RouteWrapperEffectsHandler) and must not survive a page load: a new
  // OAuth popup used to rehydrate the previous popup's `response: 'success'`
  // and close itself on its first render. Version 16 strips the stored keys.
  version: 16,
  migrations: { 16: dropPersistedRouteState },
  whitelist: ['filters', 'prefs', 'aiChat', 'helpContextMap', 'signupState', 'controls'],
  transforms: [
    createTransform(
      (state, key) => {
        const newState = { ...state };
        if (key === 'filters') {
          for (const filterKey in filterTimeouts) {
            const diff = new Date() - new Date((state.timestamps || {})[filterKey]);
            if (diff > filterTimeouts[filterKey]) {
              delete newState[filterKey];
            }
          }
        }
        if (key === 'prefs') {
          const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000;
          const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
          const currentTime = new Date().getTime();

          for (const [key, value] of Object.entries(state)) {
            const isStepCollapse = key.startsWith('step-collapsed');
            if (isStepCollapse && currentTime - new Date(value).getTime() > TWO_WEEKS_IN_MS) {
              delete newState[key];
            }

            const isLockWorkflow = key.startsWith('lock');
            if (isLockWorkflow && currentTime - new Date(value?.timestamp).getTime() > ONE_DAY_IN_MS) {
              delete newState[key];
            }

            const isCopiedSteps = key.startsWith('copied-steps');
            if (isCopiedSteps && currentTime - new Date(value?.timestamp).getTime() > ONE_DAY_IN_MS) {
              delete newState[key];
            }
          }
        }
        return newState;
      },
      (state) => state,
      { whitelist: ['filters', 'prefs'] }
    )
  ]
};

export default createPersistedReducer(
  initialState,
  {
    SET_PARAMS: (state, payload) => ({
      ...state,
      params: payload
    }),

    uiSetActiveRouteId: (state, payload) => ({
      ...state,
      activeRouteId: payload
    }),

    uiSetActiveRouteReady: (state, bool) => ({
      ...state,
      activeRouteReady: bool
    }),

    uiSetFormInvalidField: (state, payload) => ({
      ...setDeep(state, `boundForms.${payload.formId}.invalids`, (n) => [...(n || [])].filter((n) => n.id !== payload.fieldId).concat({ id: payload.fieldId, message: payload.message }))
    }),
    uiRemoveFormInvalidField: (state, payload) => ({
      ...setDeep(state, `boundForms.${payload.formId}.invalids`, (n) => [...(n || [])].filter((n) => n.id !== payload.fieldId))
    }),
    uiSetFormEmptyField: (state, payload) => ({
      ...setDeep(state, `boundForms.${payload.formId}.empties`, (n) => [...(n || [])].filter((n) => n !== payload.fieldId).concat(payload.fieldId))
    }),
    uiRemoveFormEmptyField: (state, payload) => ({
      ...setDeep(state, `boundForms.${payload.formId}.empties`, (n) => [...(n || [])].filter((n) => n !== payload.fieldId))
    }),
    uiPatchFormState: (state, payload) => ({
      ...setDeep(state, `boundForms.${payload.formId}.state`, (n) => ({
        ...n,
        ...payload.state
      }))
    }),
    uiPutFormState: (state, payload) => ({
      ...setDeep(state, `boundForms.${payload.formId}.state`, () => ({
        ...payload.state
      }))
    }),
    uiClearFormState: (state, formId) => ({
      ...setDeep(state, `boundForms.${formId}`, () => null)
    }),
    uiToggleFormValidation: (state, payload) => ({
      ...setDeep(state, `boundForms.${payload.formId}.validating`, () => payload.validating)
    }),
    uiToggleFormReset: (state, payload) => ({
      ...setDeep(state, `boundForms.${payload.formId}.reset`, () => payload.reset)
    }),
    uiPatchFilters: (state, payload) => ({
      ...state,
      filters: {
        ...state.filters,
        ...payload,
        timestamps: {
          ...(state.filters.timestamps || {}),
          ...Object.fromEntries(Object.entries(payload).map((n) => [n[0], new Date()]))
        }
      }
    }),
    uiResetPatchFilters: (state) => ({ ...state, filters: initialState.filters }),
    uiSetFixedWidth: (state, bool) => ({ ...state, fixedWidth: bool }),

    uiSetCodeState: (state, payload) => ({
      ...state,
      codeState: {
        ...state?.codeState,
        ...payload
      }
    }),

    uiSetHeaderInputState: (state, payload) => ({
      ...state,
      headerInputState: {
        ...state?.headerInputState,
        ...payload
      }
    }),

    uiSetPref: (state, prefs) => ({
      ...state,
      prefs: Object.entries(prefs || {}).reduce(
        (nextPrefs, [path, value]) => {
          const normalizedPath = String(path || '').trim();

          if (!normalizedPath) {
            return nextPrefs;
          }

          return setDeep(nextPrefs, normalizedPath, value);
        },
        { ...(state?.prefs || {}) }
      )
    }),

    uiSetSearch: (state, search) => ({
      ...state,
      search: {
        ...state?.search,
        ...search
      }
    }),

    uiSetAiTip: (state, payload) => ({
      ...state,
      prefs: {
        ...state?.prefs,
        aiTip: { ...(state?.prefs?.aiTip || {}), ...payload }
      }
    }),

    uiRemovePref: (state, { key }) => {
      const normalizedPath = String(key || '').trim();
      const newPrefs = normalizedPath ? setDeep({ ...(state?.prefs || {}) }, normalizedPath, undefined) : { ...(state?.prefs || {}) };
      return { ...state, prefs: newPrefs };
    },

    uiClearSearch: (state) => ({
      ...state,
      search: {}
    }),

    uiSetAiPopoverOpen: (state, payload) => ({
      ...state,
      aiPopoverOpen: { ...state.aiPopoverOpen, ...payload }
    }),

    uiSetAiChat: (state, payload) => ({
      ...state,
      aiChat: {
        ...state.aiChat,
        ...payload
      }
    }),

    uiClearAiChat: (state) => ({
      ...state,
      aiChat: {
        ...state.aiChat,
        messages: []
      }
    }),

    uiToggleAiChat: (state) => ({
      ...state,
      aiChat: {
        ...state.aiChat,
        open: !state.aiChat.open
      }
    }),

    uiSendAiChatMessage: (state, payload) => {
      const updatedMessages = [...state.aiChat.messages];

      if (payload.context) {
        updatedMessages.push({ content: payload.context, role: 'system' });
      }

      updatedMessages.push({ content: payload.message, role: 'user' });

      return {
        ...state,
        aiChat: {
          ...state.aiChat,
          open: true,
          messages: updatedMessages
        }
      };
    },

    uiSetCommentChat: (state, payload) => ({
      ...state,
      commentChat: {
        ...state.commentChat,
        ...payload
      }
    }),

    uiSetAiContext: (state, payload) => ({
      ...state,
      aiContext: {
        ...state.aiContext,
        ...payload
      }
    }),

    uiHighlightPanel: (state, payload) => ({
      ...state,
      highlightPanel: payload
    }),

    uiPanelHeaderAlert: (state, payload) => ({
      ...state,
      panelHeaderAlert: payload
    }),

    uiSetWorkflowZoom: (state, payload) => ({
      ...state,
      workflowZoom: payload
    }),

    uiSetWorkflowPan: (state, payload) => ({
      ...state,
      workflowPan: { ...state.workflowPan, ...payload }
    }),

    SET_CONTEXT_MENU_LISTENER: (state, payload) => ({
      ...state,
      contextMenu: mergeByProp('id', [...state.contextMenu], [payload])
    }),

    SET_CONTEXT_MENU_LISTENERS: (state, payload) => ({
      ...state,
      contextMenu: mergeByProp('id', [...state.contextMenu], payload)
    }),

    SET_CONTEXT_MENU_TARGET: (state, payload) => ({
      ...state,
      contextMenuTarget: payload
    }),

    SET_MODAL_OPEN: (state, modal) => ({
      ...state,
      modals: {
        ...state?.modals,
        ...modal
      }
    }),

    REGISTER_MODAL: (state, payload) => {
      const modalId = payload;
      const newStack = [...state.modalStack];
      if (!newStack.includes(modalId)) {
        newStack.push(modalId);
      }
      return {
        ...state,
        modalStack: newStack
      };
    },

    UNREGISTER_MODAL: (state, payload) => {
      const modalId = payload;
      return {
        ...state,
        modalStack: state.modalStack.filter((id) => id !== modalId)
      };
    },

    SET_DOC_DRAWER_OPEN: (state, payload) => ({
      ...state,
      docDrawer: {
        ...state?.docDrawer,
        ...payload
      }
    }),

    SET_APP_DOC_DRAWER_OPEN: (state, payload) => ({
      ...state,
      appDocDrawer: {
        ...state?.appDocDrawer,
        ...payload
      }
    }),

    SET_LIVE_WORKFLOW_LOGS_DRAWER_OPEN: (state, payload) => ({
      ...state,
      liveWorkflowLogsDrawer: {
        ...state?.liveWorkflowLogsDrawer,
        ...payload
      }
    }),

    SET_APP_OUTPUT_DRAWER: (state, payload) => ({
      ...state,
      appOutputDrawer: {
        ...state?.appOutputDrawer,
        ...payload
      }
    }),

    SET_APP_ISSUE_REPORT_DRAWER: (state, payload) => ({
      ...state,
      appIssueReportDrawer: {
        ...state?.appIssueReportDrawer,
        ...payload
      }
    }),

    SET_DRAWER_OPEN: (state, drawer) => ({
      ...state,
      drawers: {
        ...state?.drawers,
        ...drawer
      }
    }),

    SET_WORKFLOW_PANEL_EXPANDED: (state, bool) => ({
      ...state,
      workflowPanelIsExpanded: bool
    }),

    SET_WORKFLOW_LOCK_TRIGGERED: (state, bool) => ({
      ...state,
      workflowLockTriggered: bool
    }),

    SET_TEMP_BLOCK: (state, bool) => ({
      ...state,
      tempBlock: bool
    }),

    SET_IS_DEPLOYING: (state, payload) => ({
      ...state,
      isDeploying: {
        ...state?.isDeploying,
        ...payload
      }
    }),

    setLoader: (state, payload) => ({
      ...state,
      loadState: {
        ...state?.loadState,
        [payload.key]: { isLoading: payload.isLoading, loadStart: payload.loadStart }
      }
    }),

    WEBSOCKET_CONNECTION_SUCCESS: (state, payload) => {
      const newState = {
        ...state,
        websocket: {
          ...state.websocket,
          connectionStatus: payload.state,
          error: {}
        }
      };

      // reset error splash if it has error with socket id
      if (state.errorSplashScreen.id === 'socket') {
        newState.errorSplashScreen = {
          id: '',
          open: false,
          message: '',
          actions: []
        };
      }

      return newState;
    },

    WEBSOCKET_CONNECTION_ERROR: (state, payload) => ({
      ...state,
      websocket: {
        ...state.websocket,
        connectionStatus: payload.state,
        error: payload
      }
    }),

    UPDATE_ERROR_SPLASH_SCREEN: (state, { open, ...rest } = {}) => {
      if (open === false) {
        return {
          ...state,
          errorSplashScreen: {
            id: '',
            open: false,
            message: '',
            actions: []
          }
        };
      }

      return {
        ...state,
        errorSplashScreen: {
          ...state.errorSplashScreen,
          ...rest,
          open
        }
      };
    },

    SET_EXTERNAL_UPDATES: (state, payload) => {
      return {
        ...state,
        externalUpdates: mergeByProp('key', [...state.externalUpdates], [...payload])
      };
    },

    GET_USER_COMPANIES_OPTIONS_SUCCESS: (state, payload) => ({
      ...state,
      prefs: {
        ...state.prefs,
        recentCompanies: mergeByProp('id', state?.prefs?.recentCompanies || [], payload?.data?.recentCompanies || [], (existing, incoming) => ({
          ...incoming,
          timestamp: existing.timestamp || Date.now()
        }))
      }
    }),

    GET_USER_STATE_SUCCESS: (state, payload) => {
      const remotePrefs = payload?.data?.prefs || {};
      const mergedPrefs = mergeDeepMissing(state?.prefs || {}, remotePrefs);

      return {
        ...state,
        timezones: payload?.data?.timezones,
        helpContextMap: payload?.data?.helpContextMap,
        prefs: mergedPrefs
      };
    },

    SET_ARGUMENT_OPTION_SLIDER: (state, payload) => ({
      ...state,
      argumentOptionSlider: {
        ...state.argumentOptionSlider,
        ...payload
      }
    }),

    SET_AUDIT_LOG_SLIDER: (state, payload) => ({
      ...state,
      auditLogSlider: {
        ...state.auditLogSlider,
        ...payload
      }
    }),

    POST_AI_CHAT_MESSAGE_SUCCESS: (state, payload) => ({
      ...state,
      aiChat: {
        ...state.aiChat,
        messages: [...state.aiChat.messages, { content: payload?.text ? payload.text : 'There was an error, please try again', role: 'assistant' }]
      }
    }),

    SET_ARGUMENT_OPTION_HAS_BEEN_MODIFIED: (state, payload) => ({
      ...state,
      argumentOptionHasBeenModified: payload
    }),

    OPEN_LIVE_WORKFLOW_LOGS_DRAWER: (state) => ({
      ...state,
      liveWorkflowLogsDrawer: {
        ...state.liveWorkflowLogsDrawer,
        open: true
      }
    }),

    CLOSE_LIVE_WORKFLOW_LOGS_DRAWER: (state) => ({
      ...state,
      liveWorkflowLogsDrawer: {
        ...state.liveWorkflowLogsDrawer,
        open: false
      }
    }),
    SET_TOOLBAR_HOVERED: (state, payload) => ({
      ...state,
      isToolbarHovered: payload
    }),
    SET_TOOLBAR_POPOVER_OPEN: (state, payload) => ({
      ...state,
      isToolbarPopoverOpen: payload
    }),

    GET_PUB_SUB_MESSAGE_aiActionUpdate: (state, payload) => ({
      ...state,
      aiActionUpdate: { ...payload?.data }
    }),

    GET_PUB_SUB_MESSAGE_appActionResponseUpdate: (state, payload) => ({
      ...state,
      appActionResponseUpdate: { ...payload?.data }
    }),

    CLEAR_APP_ACTION_RESPONSE_UPDATE: (state) => ({
      ...state,
      appActionResponseUpdate: {}
    }),

    GET_PUB_SUB_MESSAGE_appFixerUpdate: (state, payload) => ({
      ...state,
      appFixerUpdate: { ...payload?.data }
    }),

    SET_RUN_STEP: (state, payload) => ({
      ...state,
      runStep: payload
    }),

    SET_TRANSITION_SIGN_UP: (state, payload) => ({
      ...state,
      transitionSignUp: payload
    }),

    SET_SIGNUP_BOOTSTRAP_ERROR: (state, payload) => ({
      ...state,
      signupBootstrapError: payload
    }),

    RETRY_SIGNUP_BOOTSTRAP: (state, payload) => ({
      ...state,
      signupBootstrapError: null,
      signupBootstrapRetry: {
        sequence: state.signupBootstrapRetry.sequence + 1,
        stage: payload.stage
      },
      transitionSignUp: true
    }),

    SET_SIGNUP_STATE: (state, payload) => ({
      ...state,
      signupState: {
        ...state.signupState,
        ...payload
      }
    }),

    CLEAR_SIGNUP_STATE: (state, payload = {}) => ({
      ...state,
      signupState: { ...initialState.signupState, ...payload }
    }),

    SET_LOOKUP_LOADING: (state, payload) => ({
      ...state,
      lookupLoading: { ...state.lookupLoading, ...payload }
    }),

    GET_DOMAIN_DISCOVERY_SUCCESS: (state, payload) => ({
      ...state,
      domainDiscovery: payload?.data
    }),

    CLEAR_DOMAIN_DISCOVERY: (state) => ({
      ...state,
      domainDiscovery: []
    }),

    SET_GOOGLE_SHEETS_COLUMNS: (state, payload) => ({
      ...state,
      googleSheetsColumns: {
        ...state.googleSheetsColumns,
        ...payload
      }
    }),

    SET_CONTROLS: (state, payload) => ({
      ...state,
      controls: { ...state?.controls, ...payload }
    }),

    SET_ACTION_PAGE_RUN_STATE: (state, payload) => ({
      ...state,
      actionPageRunState: {
        ...state?.actionPageRunState,
        ...payload
      }
    }),

    SET_CONNECTION_POPOVER_OPEN: (state, payload) => ({
      ...state,
      connectionPopoverOpen: payload
    }),

    SET_USE_NEXT_WEBHOOK_RESPONSE: (state, payload) => ({
      ...state,
      useNextWebhookResponse: payload
    }),

    GET_PUB_SUB_MESSAGE_webhookResponseUpdate: (state, payload) => ({
      ...state,
      useNextWebhookResponse: state?.useNextWebhookResponse === 'pending' ? true : state?.useNextWebhookResponse
    })
  },
  persistConfig
);
